import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import styled from 'styled-components';
import {
  Button,
  Flexbox,
  TextInput,
  SingleSelect
} from '@ibinder/common-frontend';
import ListDocument from '../ListDocument';
import { assignmentsQuery, GET_USER_ASSIGNMENTS } from './getUserAssignments';
import {
  AssignmentsTarget,
  UserAssignment,
  UserAssignmentsByOrganizationResponse
} from './userResourceAccess.types';

type Props = {
  userId: string;
  userName: string;
  target: AssignmentsTarget;
  onBack: () => void;
};

type SortBy = {
  id?: string;
  desc: boolean | null;
};

type AssignmentRow = {
  id: string;
  assignmentType: string;
  resourceName: string;
  status: string;
  statusCode: string;
  dueDate: string;
  responsibility: string;
  binderName: string;
  binderId: string;
};

const ALL_OPTION = 'All';

const Container = styled.article`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.25rem;
  margin: 0 0 1rem 0;
`;

const ResourceCard = styled(Flexbox)`
  border: 0.0625rem solid ${({ theme }) => theme.colors.primary30};
  border-radius: 0.25rem;
  gap: 2.5rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
`;

const ResourceProperty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PropertyLabel = styled.span`
  color: ${({ theme }) => theme.colors.discrete};
  font-size: 0.75rem;
`;

const PropertyValue = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const Filters = styled(Flexbox)`
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SearchInput = styled(TextInput)`
  max-width: 18rem;
`;

const FilterSelect = styled(SingleSelect)`
  min-width: 12rem;
`;

const ClearFiltersButton = styled(Button)`
  font-size: 0.875rem;
  height: 2rem;
  min-width: auto;
  width: auto;
`;

const ResultCount = styled.p`
  color: ${({ theme }) => theme.colors.discrete};
  margin: 0 0 0.5rem 0;
`;

type StatusTone = 'neutral' | 'warning' | 'error' | 'success';

const StatusPill = styled.span<{ $tone: StatusTone }>`
  background: ${({ theme, $tone }) =>
    $tone === 'error'
      ? theme.colors.error
      : $tone === 'success'
      ? theme.colors.success
      : $tone === 'warning'
      ? '#f59e0b'
      : theme.colors.primary10};
  border-radius: 1rem;
  color: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.primary : '#fff'};
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.125rem 0.625rem;
  white-space: nowrap;
`;

const BackButtonContainer = styled(Flexbox)`
  justify-content: flex-start;
  margin-top: auto;
  padding-top: 1rem;
`;

const BackButton = styled(Button)`
  font-size: 0.875rem;
  height: 2rem;
  min-width: auto;
  width: auto;
`;

const StatusMessage = styled.p`
  margin: 1rem 0;
`;

const statusTone = (statusCode: string): StatusTone => {
  const code = (statusCode || '').toLowerCase();
  if (code.indexOf('overdue') >= 0) {
    return 'error';
  }
  if (code.indexOf('due') >= 0 || code.indexOf('progress') >= 0) {
    return 'warning';
  }
  if (code.indexOf('complete') >= 0 || code.indexOf('closed') >= 0) {
    return 'success';
  }
  return 'neutral';
};

// Domains come back lowercase, for example "issues" or "inspections".
const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

const formatDueDate = (dueDate: string | null) => {
  if (!dueDate) {
    return '';
  }
  const parsed = new Date(dueDate);
  return isNaN(parsed.getTime()) ? dueDate : parsed.toLocaleDateString();
};

const toAssignmentRow = (assignment: UserAssignment): AssignmentRow => ({
  id: assignment.resourceId,
  assignmentType: capitalize(assignment.domain),
  resourceName: assignment.resourceName || '',
  status: assignment.status?.label || assignment.status?.code || '',
  statusCode: assignment.status?.code || '',
  dueDate: formatDueDate(assignment.dueDate),
  responsibility:
    assignment.responsibility?.label || assignment.responsibility?.code || '',
  binderName: assignment.binder?.name || '',
  binderId: assignment.binder?.id || ''
});

const assignmentColumns = [
  {
    Header: 'Assignment type',
    accessor: 'assignmentType',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Resource name',
    accessor: 'resourceName',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Binder',
    accessor: 'binderName',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Binder Id',
    accessor: 'binderId',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Status',
    accessor: 'status',
    sortType: 'alphanumeric',
    Cell: ({ row }) =>
      row.original.status ? (
        <StatusPill $tone={statusTone(row.original.statusCode)}>
          {row.original.status}
        </StatusPill>
      ) : (
        ''
      )
  },
  {
    Header: 'Due date',
    accessor: 'dueDate',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Role / Responsibility',
    accessor: 'responsibility',
    sortType: 'alphanumeric'
  }
];

const uniqueValues = (values: string[]) =>
  values
    .filter((value, index) => value && values.indexOf(value) === index)
    .sort((a, b) => a.localeCompare(b));

const UserAssignments = ({ userId, userName, target, onBack }: Props) => {
  const [sortBy, setSortBy] = useState<SortBy>({
    id: 'dueDate',
    desc: false
  });
  const [search, setSearch] = useState('');
  const [typeIndex, setTypeIndex] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);
  const [loadAssignments, { data, loading, error, called }] = useLazyQuery(
    GET_USER_ASSIGNMENTS,
    { fetchPolicy: 'no-cache' }
  );

  useEffect(() => {
    setSearch('');
    setTypeIndex(0);
    setStatusIndex(0);
    loadAssignments({
      variables: {
        userId,
        body: [target.organizationId],
        query: assignmentsQuery(
          target.kind === 'Binder' ? target.id : undefined
        )
      }
    });
  }, [userId, target.kind, target.id, target.organizationId]);

  const rows = useMemo(() => {
    const response = data?.getUserAssignments as
      | UserAssignmentsByOrganizationResponse
      | undefined;
    return (response?.byOrganization || []).reduce(
      (collected, entry) =>
        collected.concat((entry.assignments || []).map(toAssignmentRow)),
      [] as AssignmentRow[]
    );
  }, [data]);

  const typeOptions = useMemo(
    () => [ALL_OPTION, ...uniqueValues(rows.map((row) => row.assignmentType))],
    [rows]
  );
  const statusOptions = useMemo(
    () => [ALL_OPTION, ...uniqueValues(rows.map((row) => row.status))],
    [rows]
  );

  const selectedType = typeOptions[typeIndex] || ALL_OPTION;
  const selectedStatus = statusOptions[statusIndex] || ALL_OPTION;
  const searchTerm = search.trim().toLowerCase();

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const matchesSearch =
          !searchTerm ||
          row.resourceName.toLowerCase().indexOf(searchTerm) >= 0 ||
          row.binderName.toLowerCase().indexOf(searchTerm) >= 0 ||
          row.binderId.toLowerCase().indexOf(searchTerm) >= 0;
        const matchesType =
          selectedType === ALL_OPTION || row.assignmentType === selectedType;
        const matchesStatus =
          selectedStatus === ALL_OPTION || row.status === selectedStatus;
        return matchesSearch && matchesType && matchesStatus;
      }),
    [rows, searchTerm, selectedType, selectedStatus]
  );

  const isLoading = !called || loading;
  const hasFilters =
    !!searchTerm ||
    selectedType !== ALL_OPTION ||
    selectedStatus !== ALL_OPTION;

  const clearFilters = () => {
    setSearch('');
    setTypeIndex(0);
    setStatusIndex(0);
  };

  return (
    <Container>
      <Title>Assignment Details</Title>

      <ResourceCard>
        <ResourceProperty>
          <PropertyLabel>Resource</PropertyLabel>
          <PropertyValue>{target.name}</PropertyValue>
        </ResourceProperty>
        <ResourceProperty>
          <PropertyLabel>{`${target.kind} id`}</PropertyLabel>
          <PropertyValue>{target.id}</PropertyValue>
        </ResourceProperty>
        <ResourceProperty>
          <PropertyLabel>Type</PropertyLabel>
          <PropertyValue>{target.kind}</PropertyValue>
        </ResourceProperty>
        <ResourceProperty>
          <PropertyLabel>Organization</PropertyLabel>
          <PropertyValue>{target.organizationName}</PropertyValue>
        </ResourceProperty>
        <ResourceProperty>
          <PropertyLabel>Role</PropertyLabel>
          <PropertyValue>
            {target.participantType || 'No direct access'}
          </PropertyValue>
        </ResourceProperty>
        <ResourceProperty>
          <PropertyLabel>User</PropertyLabel>
          <PropertyValue>{userName}</PropertyValue>
        </ResourceProperty>
      </ResourceCard>

      <Filters>
        <SearchInput
          name="assignmentSearch"
          label="Search assignments"
          placeholder="Search assignments..."
          value={search}
          changeValue={(event) => setSearch(event?.target?.value || '')}
        />
        <FilterSelect
          label="Assignment type"
          dropdownText="All"
          activeIndex={typeIndex}
          dropdownData={typeOptions.map((option) => ({
            Key: option,
            Value: option
          }))}
          onClickSelectItem={setTypeIndex}
          withSortButton={false}
        />
        <FilterSelect
          label="Status"
          dropdownText="All"
          activeIndex={statusIndex}
          dropdownData={statusOptions.map((option) => ({
            Key: option,
            Value: option
          }))}
          onClickSelectItem={setStatusIndex}
          withSortButton={false}
        />
        <ClearFiltersButton
          btnStyle="secondary"
          label="Clear filters"
          disabled={!hasFilters}
          onClick={clearFilters}
        />
      </Filters>

      {error ? (
        <StatusMessage>Unable to load assignments.</StatusMessage>
      ) : !isLoading && rows.length === 0 ? (
        <StatusMessage>{`This ${target.kind.toLowerCase()} has no assignments for ${userName}.`}</StatusMessage>
      ) : (
        <>
          <ResultCount>
            {isLoading
              ? 'Loading assignments...'
              : `Showing ${filteredRows.length} of ${rows.length} assignments`}
          </ResultCount>
          <ListDocument
            data={filteredRows}
            columns={assignmentColumns}
            onSort={setSortBy}
            sortBy={sortBy}
            selectedIds={[]}
            setSelectedIds={() => undefined}
            hasSelectBox={false}
            fetchMore={() => undefined}
            hasFetchMore={() => false}
            loading={isLoading}
            type="UserAssignments"
            truncateFirstColumn={false}
            fullContentColumnIds={['resourceName', 'binderName']}
          />
        </>
      )}

      <BackButtonContainer>
        <BackButton btnStyle="secondary" label="Back" onClick={onBack} />
      </BackButtonContainer>
    </Container>
  );
};

export default UserAssignments;
